import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PcpEntity, PcpEvent } from '@pcp/shared';
import { eventsStreamUrl, getChanges } from '../lib/api';
import { useDataFreshnessStore, useConnectionStore, type DataDomain, type ConnStatus } from '../lib/stores';

// ─── Realtime UI sync ────────────────────────────────────────────
// One headless EventSource to /api/events. Every change nudge is routed through
// the SAME focus/drag-safe React Query path the sync watcher uses (SyncLogPanel):
// invalidateQueries background-refetches active LIST queries so React reconciles
// new data WITHOUT remounting — focused inputs keep focus, lists don't jump — and
// Backlog refetches are DEFERRED while a drag is in progress, then flushed on drop.
// Detail pages are NOT auto-yanked: an id-ful event records per-entity freshness
// (markEntity) so a detail page for THAT item shows a StaleDataBanner / edit-conflict
// warning; an id-less or reconnect event bumps the whole domain (coarse fallback).

// Entity → the list query-key prefixes to refetch + the detail-page domain to flag.
// NOTE: detail-page keys (azure-work-item / zoho-ticket / discourse-topic) are
// deliberately NOT invalidated here — those pages own their refresh via the
// per-entity freshness (StaleDataBanner), so they offer a manual refresh / a
// silent self-refetch instead of auto-yanking what the user is reading/editing.
// `detail` (when set) is the react-query key prefix for that entity's DETAIL page,
// keyed `[detail, id]` — or an ARRAY of prefixes when one entity backs more than one
// detail/edit surface (see `article`: the KB editor and the page-spec editor read the
// same table under different keys). Detail queries cache ~10min (DETAIL_CACHE) so
// opening a related item and returning is instant; an id-ful live event busts the
// EXACT changed item's cached detail so re-opening it refetches (see applyEvent).
// `rails` are the id-keyed related-item / Mentions resolver queries a detail page
// renders alongside the main item. They cache ~10min (DETAIL_CACHE), so — unlike
// the id-precise `detail` bust — a live change to ANY item can affect ANOTHER
// item's rail (a cross-reference added/removed, a referenced item edited), so we
// invalidate the whole rail prefix (not id-scoped) on every event of that entity.
// `detailLive` opts an entity OUT of the "never yank an open detail page" default
// above: its detail query is invalidated for real, so the open page refetches now
// instead of only marking itself stale. Set it ONLY where the open surface can
// absorb a refetch without losing work — the flag exists so that judgement is made
// per entity and written down, rather than special-cased inside applyEvent.
export const ENTITY_MAP: Record<PcpEntity, { lists: string[]; domain?: DataDomain; detail?: string | string[]; detailLive?: boolean; rails?: string[] }> = {
  // NOTE: 'global-search' is deliberately NOT a rail here. Rails are invalidated
  // unscoped on every event of their entity, which is right for the cheap
  // id-keyed resolvers they were built for and wrong for a search: backlog sync
  // emits events continuously, so an AI search re-ran — a planning call, an
  // embedding per concept, an answering call — because somebody else's work item
  // changed. A result list is a snapshot of a question a person typed, not a live
  // view of a record. (b-01838)
  // 'backlog-feedback' is the item's Feedback tab (the boards linked to it). It is
  // a RAIL of the backlog item, not a list of its own: a link or unlink emits a
  // backlog event (routes/feedback.ts emitBacklogLink), and without it the tab
  // still showed a board somebody had just unlinked. It is ALSO in `feedback`'s
  // lists below, which is what covers the other direction — a board renamed,
  // re-stated or deleted elsewhere. (f-01920)
  backlog:  { lists: ['azure-work-items', 'azure-facets', 'azure-board-lane'], domain: 'azure', detail: 'azure-work-item', rails: ['backlog-mention-summaries', 'related-items-resolve', 'backlog-feedback'] },
  // 'filter-values' / 'filter-count' (the filter framework's picker counts, f-02064)
  // ride on ticket events too, so a sync refreshes the counts behind the pills.
  ticket:   { lists: ['zoho-tickets', 'zoho-facets', 'filter-values', 'filter-count'], domain: 'zoho', detail: 'zoho-ticket', rails: ['zoho-ticket-related'] },
  topic:    { lists: ['discourse-topics', 'discourse-facets'], domain: 'discourse', detail: 'discourse-topic', rails: ['discourse-topic-workitems'] },
  // Feedback boards: the list + facets, and the board detail — which is LIVE
  // (`detailLive`), unlike every other detail page here. An open board refetches
  // the moment anything on it changes, wherever the change came from: another
  // user, the REST API, or an MCP tool.
  //
  // It can afford to, and the others cannot, because the canvas already defends
  // itself against a refetch landing on it: a board arriving mid-gesture is HELD
  // until the gesture ends (b-01871), and un-flushed local edits — including the
  // text of a sticky note being typed — are laid back over the rebuilt model
  // (b-01868). The comment editor, the one surface neither covers, is guarded by
  // the standard edit-conflict banner via the 'feedback' domain below.
  //
  // Without this the board only refreshed on navigate-away-and-back, which left a
  // pasted link card sitting on `pending` forever — f-01893's unfurl fills the row
  // in out of band and relies on exactly this push to show it. (b-01911)
  //
  // The canvas's own debounced position writes still deliberately emit nothing, so
  // panning or dragging a board never invalidates anyone.
  //
  // `azure-work-item` and `feature-request` are DETAIL keys sitting in a list set,
  // deliberately. The Feedback tab's badge — boards, and threads still open across
  // them (f-01930) — does not live on the board: it rides on the OWNER's payload,
  // which is what makes the tab strip cost no extra request. So a comment going
  // Done has to refetch the owner, or the number sits there stale until reload.
  //
  // It cannot be left to `bustCrossRefDetails`, for two independent reasons: that
  // only runs for PRIMARY entities (feedback is not one), and it invalidates with
  // `refetchType: 'none'` so an open page is never yanked — right for a rail
  // holding a stale snapshot, wrong for a number that is meant to be live.
  //
  // Every way the counts can move already emits a feedback event: a comment
  // created, its state changed ('commented'), deleted; a board created or
  // deleted; and link/unlink, which emits one alongside its backlog event. Driving
  // it from the EVENT rather than from the writer is what makes a colleague's
  // change show up too. Cost is one refetch of whichever owner is on screen.
  // (b-01932)
  //
  // `request-feedback` is the LAST key a feedback event failed to reach: it is the
  // board embedded on a request page, keyed ['request-feedback', requestId] by the
  // request rather than by the board's own id (components/requests/
  // RequestFeedbackBoard.tsx), so no amount of id-busting on 'feedback' finds it.
  // 'feature-request' above already refreshes the Feedback tab's badge and the
  // Details-tab note, but the embedded canvas paints `data ?? draft` — until the
  // board row exists there is nothing to invalidate, and an OPEN request page goes
  // on painting its empty client-side draft. That is precisely the case this whole
  // flow was built for: an agent files a request, then uploads a screenshot to it
  // over MCP through a signed upload URL, and the board is created lazily by the
  // FIRST file that arrives — so the person watching the page must see the
  // screenshot appear, not learn to reload. (f-01941)
  feedback: { lists: ['feedback-list', 'feedback-facets', 'backlog-feedback', 'azure-work-item', 'feature-request', 'request-feedback'], domain: 'feedback', detail: 'feedback', detailLive: true },
  request:  { lists: ['feature-requests', 'feature-request', 'feature-request-stats', 'requests-unseen'], domain: 'requests' },
  // Release notes: list + detail + the topbar what's-new indicator. No detail-page
  // freshness domain (the cards refetch via the list invalidation).
  release:  { lists: ['release-notes', 'release-note', 'requests-unseen'] },
  // Per-user and per-user only: notify() emits this scoped to ONE recipient, so
  // it never tells the rest of the org that somebody got a notification. Both
  // keys are listed because the badge and the feed are separate queries.
  notification: { lists: ['notifications', 'notifications-unread'] },
  settings: { lists: ['settings'] },
  profile:  { lists: ['profile'] },
  // per-user reviewed flag lives on the work-item list rows (scope-filtered server-side)
  review:   { lists: ['azure-work-items', 'azure-board-lane'] },
  // Knowledge articles: list + detail + the embedding-coverage status/feed. Pushed
  // on create/update/publish/archive/delete and on embed prepared/failed — replaces
  // the old 1.5s KB-list poll. Page-specs (docType 'page-spec') ride this SAME
  // entity:'article' event (same table, no docType in the payload), so the Solution
  // Design surfaces are listed here too — else a spec rename/move never reaches
  // another tab/user until a hard refresh:
  //   'specs'         Solution Design flat list ("All specifications")
  //   'spec-folders'  Solution Design folder tree — a move or a rename changes the
  //                   per-folder counts, so the TREE is stale even when the spec that
  //                   moved isn't on screen
  //   'spec-tags'     the tag vocabulary + counts behind the tag FILTER — editing a
  //                   spec's tags can add or retire a whole tag
  // Reader is keyed ['kb-article-by-slug', slug] (list-invalidated → silent refetch);
  // the EDITORS' ['kb-article', id] (KB) and ['kb-spec', id] (page spec) are the
  // `detail` keys (id-busted with refetchType:'none') so a remote change never
  // ACTIVE-refetches the open editor and clobbers unsaved edits — they react via their
  // own useStaleData('knowledge') conflict banner. 'kb-spec' used to sit in `lists`,
  // which made every remote article event ACTIVE-refetch the spec the user had open —
  // exactly what PageSpec's `refetchOnWindowFocus: false` exists to prevent.
  article:  {
    domain: 'knowledge',
    detail: ['kb-article', 'kb-spec'],
    lists: ['kb-articles', 'kb-article-by-slug', 'specs', 'spec-folders', 'spec-tags', 'knowledge-status', 'embed-activity'],
  },
  // Sync runs: pushed on run start + terminal transition — replaces the 2s /api/syncs
  // poll. Also refreshes document/knowledge-status counts a sync may have changed.
  sync:     { lists: ['sync-runs', 'knowledge-status', 'documents'] },
  // App error log: pushed on every new/updated/resolved error → live Diagnostics page.
  errors:   { lists: ['app-errors'] },
  // User audit "moves": pushed per recorded action → live-refreshes an open User
  // administration detail page (overview + the user's detail/audit queries).
  audit:    { lists: ['user-admin'] },
  // Companies (Zoho CRM, F3). Forward-looking keys for the /companies area; the
  // detail page + tabs are wired when the UI lands. No freshness `domain` yet
  // (added with the detail page's useStaleData). CRM data is org-visible (scope:'all').
  crm:      { lists: ['crm-companies', 'crm-facets'], detail: 'crm-company', rails: ['crm-company-contacts', 'crm-company-deals', 'crm-company-notes'] },
  // Communications Journal (F4.2). Company-visible journal entries (scope:'all');
  // forward-looking keys — the journal list + the company detail's journal-tab rail.
  // The pages land in F4.2c; no freshness `domain` yet.
  journal:  { lists: ['journal-entries'], rails: ['crm-company-journal'] },
  // Per-user PRIVATE triage inbox (F4.2). scope:{user} events target it, so it only
  // ever reaches the owning user's tabs. Forward-looking list key (page lands in F4.2c).
  inbox:    { lists: ['comms-inbox'] },
  // RealEquity platform topology mirror (Tenants area). Mirror data refreshes at
  // most every 15 min and detail pages use DETAIL_CACHE, so list invalidation is
  // enough — no `detail`/`domain`/`rails` (never clobbers an open detail page).
  realequity: { lists: ['re-tenants', 're-facets', 're-search'] },
  // Public status page (f-01494). The API emits `status` events on every incident
  // and component mutation so the AUTHENTICATED admin console (/status, the
  // StatusOps tabs + the incident detail) stays live. Every prefix here is a key
  // the screens actually query — a screen key that ISN'T listed goes stale ~10min
  // after a live change (b-00447/b-00448). Convention:
  // list ['status-incidents','page',filters] · detail ['status-incident', id] ·
  // ['status-facets'] · ['status-components'] · ['status-audiences'] ·
  // ['status-subscribers', …] · ['status-contact-points', …] · ['status-snapshot-state'].
  // No `domain` (the detail page has no StaleDataBanner/edit-conflict surface —
  // it silently refetches) and no `rails` (incidents cross-reference components,
  // which ride inside the detail payload the id-bust already covers).
  // The PUBLIC page itself never sees these: it is a separate Vite entry
  // (src/status/**) with no React Query, no auth and no EventSource — it polls its
  // own snapshot so it keeps working when this app's API is down.
  status:   {
    lists: [
      'status-incidents', 'status-facets', 'status-components', 'status-audiences',
      'status-subscribers', 'status-contact-points', 'status-snapshot-state',
    ],
    detail: 'status-incident',
  },
  // The common filter framework (f-02064/f-02065): saved filters + per-user filter
  // state ['saved-filters', listId], the bar layout incl. default filter and last
  // view ['bar-layout', listId], version history and reach. The API scopes these
  // events {user} for private filters, layouts and state and 'all' for shared
  // filters, so another user's private edits never reach this tab. No detail /
  // domain: the bar absorbs a refetch (the customizer edits a local draft).
  filter:   { lists: ['saved-filters', 'bar-layout', 'filter-versions', 'filter-reach'] },
};

/** An entity's DETAIL query-key prefixes (each keyed `[prefix, id]`). Most entities
 *  have exactly one; `article` has two because the same table backs two editors (the
 *  KB editor and the page-spec editor). Normalising here keeps every call site — the
 *  id-precise bust, the coarse bust and the cross-ref bust — on one code path. */
const detailKeys = (entity: PcpEntity): string[] => {
  const d = ENTITY_MAP[entity]?.detail;
  if (!d) return [];
  return Array.isArray(d) ? d : [d];
};

// The three primary entities cross-reference EACH OTHER inside their rails and
// mentions: a topic's "related items" rail (discourse-topic-workitems) embeds a
// BACKLOG item's state, a ticket's related rail (zoho-ticket-related) embeds
// topic/backlog snapshots, and mentions (resolved from body text) can be any of
// the three. So a change to ONE primary entity can stale a rail or an embedded
// mention hosted on a DIFFERENT primary entity's page. We therefore treat the
// three primary rails + details as one cross-referencing set: any primary event
// invalidates the whole set, not just the host entity's own (b-00447/b-00448 left
// only the host direction wired, so a backlog state change left the topic's
// related-items rail + mentions stale until an unrelated topic event).
const PRIMARY_ENTITIES: PcpEntity[] = ['backlog', 'ticket', 'topic'];
const PRIMARY_RAILS = Array.from(new Set(PRIMARY_ENTITIES.flatMap((e) => ENTITY_MAP[e].rails ?? [])));
const PRIMARY_DETAILS = Array.from(new Set(PRIMARY_ENTITIES.flatMap(detailKeys)));
const isPrimary = (entity: PcpEntity) => PRIMARY_ENTITIES.includes(entity);

/** The detail-page freshness domain an entity maps to. Single source of truth,
 *  reused by the edit-conflict registry so it never re-hardcodes the mapping. */
export function entityDomain(entity: PcpEntity): DataDomain | undefined {
  return ENTITY_MAP[entity]?.domain;
}

export function RealtimeBridge() {
  const queryClient = useQueryClient();
  const bump = useDataFreshnessStore((s) => s.bump);
  const markEntity = useDataFreshnessStore((s) => s.markEntity);
  const azureReorderActive = useDataFreshnessStore((s) => s.azureReorderActive);
  const setConnStatus = useConnectionStore((s) => s.setStatus);
  const noteConnEvent = useConnectionStore((s) => s.noteEvent);
  const pendingListsRef = useRef<Set<string>>(new Set());
  const lastCursorRef = useRef(0);

  // Refetch an entity's LIST queries (deferring Backlog/azure keys while a drag is
  // in progress). Reads the LIVE drag flag (getState) since this runs inside a
  // long-lived SSE handler.
  const refetchLists = useCallback((entity: PcpEntity) => {
    const m = ENTITY_MAP[entity];
    if (!m) return;
    const dragging = useDataFreshnessStore.getState().azureReorderActive;
    for (const key of m.lists) {
      if (dragging && key.startsWith('azure-')) { pendingListsRef.current.add(key); continue; }
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  // Invalidate the detail-page related-item / Mentions rails affected by this event.
  // Rails cache ~10min (DETAIL_CACHE) and hold a SNAPSHOT (state/title) of a possibly
  // DIFFERENT entity, so a change to a primary entity can stale a rail on ANY primary
  // page — we invalidate the whole primary-rail set (not id-scoped). invalidateQueries
  // refetches mounted rails now and marks unmounted ones stale → they refetch on the
  // next open, so navigating back is instant-correct. Non-primary entities use their
  // own rails only.
  const refetchRails = useCallback((entity: PcpEntity) => {
    const rails = isPrimary(entity) ? PRIMARY_RAILS : (ENTITY_MAP[entity]?.rails ?? []);
    for (const key of rails) queryClient.invalidateQueries({ queryKey: [key] });
  }, [queryClient]);

  // Mentions ride INSIDE a detail payload (topic.mentions in ['discourse-topic', id],
  // backlog relatedItems in ['azure-work-item', id]) and embed OTHER primary entities.
  // A change to one primary entity can stale another's embedded mentions, so mark the
  // OTHER primary details stale (refetchType:'none' — never yank an open page) → they
  // re-resolve on reopen / navigate-back.
  //
  // The host entity's OWN detail prefix is busted too, EXCEPT the exact changed id
  // (the caller already handled that one precisely, and re-marking it here would
  // stale the page the user is standing on). That is not redundant: backlog
  // relatedItems embeds OTHER BACKLOG ITEMS — a feature's payload carries each
  // child's state/title/assignee — so a change to a child leaves its PARENT's
  // cached detail wrong. Skipping `own` wholesale left exactly that hole: move a
  // child user story, press Back, and the feature still showed the old status
  // (b-01527).
  const bustCrossRefDetails = useCallback((entity: PcpEntity, changedId?: string | number | null) => {
    if (!isPrimary(entity)) return;
    const own = new Set(detailKeys(entity));
    for (const detail of PRIMARY_DETAILS) {
      if (own.has(detail)) {
        queryClient.invalidateQueries({
          queryKey: [detail],
          refetchType: 'none',
          predicate: (q) => changedId == null || q.queryKey[1] !== String(changedId),
        });
        continue;
      }
      queryClient.invalidateQueries({ queryKey: [detail], refetchType: 'none' });
    }
  }, [queryClient]);

  // A precise (id-ful) live event: refetch lists + rails + record per-entity freshness so a
  // detail/edit surface for that exact item can react (with the actor, for self-suppression).
  const applyEvent = useCallback((ev: PcpEvent) => {
    refetchLists(ev.entity);
    refetchRails(ev.entity);
    const m = ENTITY_MAP[ev.entity];
    // Bust the EXACT changed item's cached detail query (kept ~10min by DETAIL_CACHE)
    // so re-opening THAT item refetches while every other detail cache stays warm.
    // refetchType 'none' marks it stale without refetching now — so the page currently
    // viewing it is NOT yanked (it reacts via the StaleDataBanner / markEntity below);
    // an inactive cache just refetches on the next open. `detailLive` entities opt
    // out of that and refetch for real (see the flag's note on ENTITY_MAP).
    if (ev.id != null) {
      for (const key of detailKeys(ev.entity)) {
        queryClient.invalidateQueries(
          m?.detailLive
            ? { queryKey: [key, String(ev.id)] }
            : { queryKey: [key, String(ev.id)], refetchType: 'none' },
        );
      }
    }
    // A cross-referencing detail on another primary page — or, for backlog, on
    // ANOTHER ITEM OF THE SAME KIND (a parent feature embeds this child's state)
    // — may now be stale. Mark those stale so they re-resolve on reopen.
    bustCrossRefDetails(ev.entity, ev.id);
    const domain = m?.domain;
    if (!domain) return;
    if (ev.id != null) markEntity(domain, ev.id, ev.actorUserId, ev.change, ev.cursor, ev.ts);
    else bump(domain, ev.actorUserId); // id-less coarse change (e.g. a large sync batch)
  }, [refetchLists, refetchRails, bustCrossRefDetails, markEntity, bump, queryClient]);

  // A coarse (reconnect/resync) signal: only the entity is known (the /api/changes
  // probe carries no id/actor) → refetch lists + rails + mark the whole domain stale.
  const applyCoarse = useCallback((entity: PcpEntity) => {
    refetchLists(entity);
    refetchRails(entity);
    const m = ENTITY_MAP[entity];
    // After a reconnect / tab-return we can't prove which item changed — bust the
    // per-item detail cache prefix (refetchType:'none') so reopening a changed item
    // refetches instead of serving the ~10min DETAIL_CACHE. The open detail page
    // reacts to the domain bump below (silent refetch unless it's mid-edit).
    for (const key of detailKeys(entity)) {
      queryClient.invalidateQueries(
        m?.detailLive ? { queryKey: [key] } : { queryKey: [key], refetchType: 'none' },
      );
    }
    bustCrossRefDetails(entity);
    if (m?.domain) bump(m.domain, null);
  }, [refetchLists, refetchRails, bustCrossRefDetails, bump, queryClient]);

  // Full catch-up: invalidate EVERY entity's lists/rails + bump every domain. The
  // belt-and-suspenders fallback used only when the targeted resync probe fails on a
  // reconnect, so a dropped-then-restored stream can never leave the UI stale.
  const coarseInvalidateAll = useCallback(() => {
    for (const entity of Object.keys(ENTITY_MAP) as PcpEntity[]) applyCoarse(entity);
  }, [applyCoarse]);

  // Flush refetches deferred during a Backlog drag, once the drag ends.
  useEffect(() => {
    if (!azureReorderActive && pendingListsRef.current.size > 0) {
      for (const key of pendingListsRef.current) queryClient.invalidateQueries({ queryKey: [key] });
      pendingListsRef.current.clear();
    }
  }, [azureReorderActive, queryClient]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') return; // SSR / unsupported

    const STALE_MS = 40_000;    // > server 25s ping interval + margin
    const WATCHDOG_MS = 10_000;
    let es: EventSource | null = null;
    let disposed = false;
    let lastMessageAt = Date.now();
    let sawPing = false;
    let hasConnected = false;
    let trueStatus: ConnStatus = 'connecting';

    // Store the TRUE connection status — never the hidden-tab 'paused' overlay, which
    // ConnectionStatus derives for DISPLAY ONLY. Keeping truth in the store means a
    // drop+recover that happens entirely while the tab is hidden is still counted in
    // `reconnects` and written to the glitch log.
    const push = (next: ConnStatus) => {
      trueStatus = next;
      setConnStatus(next);
    };

    // Targeted catch-up on RE-connect: replay scopes that advanced while away.
    // Retries with backoff; on final failure, full-invalidate. NEVER swallowed silently.
    const catchUp = async (attempt = 0): Promise<void> => {
      try {
        const { cursor, scopes } = await getChanges(lastCursorRef.current);
        if (disposed) return; // unmounted mid-flight — don't invalidate after teardown
        for (const scopeKey of scopes) applyCoarse(scopeKey.split(':').pop() as PcpEntity);
        if (cursor > lastCursorRef.current) lastCursorRef.current = cursor;
      } catch {
        if (disposed) return;
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, [1000, 3000, 6000][attempt]));
          if (!disposed) await catchUp(attempt + 1);
        } else {
          coarseInvalidateAll(); // give up on the precise probe — refresh everything
        }
      }
    };

    // First connect: seed the cursor WITHOUT invalidating (no first-load storm).
    const seed = async () => {
      try {
        const { cursor } = await getChanges(lastCursorRef.current);
        if (disposed) return;
        if (lastCursorRef.current === 0) lastCursorRef.current = cursor;
      } catch { /* a miss here is covered by the next reconnect's catch-up */ }
    };

    const connect = () => {
      es?.close();
      es = new EventSource(eventsStreamUrl()); // same-origin cookie auto-sent
      lastMessageAt = Date.now();

      es.onopen = () => {
        lastMessageAt = Date.now();
        push('live');
        if (!hasConnected) { hasConnected = true; void seed(); }
        else void catchUp(); // recovery → store increments `reconnects`
      };

      es.onmessage = (e: MessageEvent) => {
        lastMessageAt = Date.now();
        let ev: PcpEvent;
        try { ev = JSON.parse(e.data as string); } catch { return; }
        if (ev.cursor > lastCursorRef.current) lastCursorRef.current = ev.cursor;
        noteConnEvent();
        applyEvent(ev);
      };

      // Named liveness ping (server sends every 25s). Arms the watchdog.
      es.addEventListener('ping', () => {
        lastMessageAt = Date.now();
        sawPing = true;
        if (trueStatus !== 'live') push('live');
      });

      // Reflect a drop — INCLUDING a first-connect failure (server down at page load
      // or during a deploy), else the dot would sit on "Connecting…" forever. Zombies
      // (onerror never fires) are handled by the watchdog below.
      es.onerror = () => { if (trueStatus === 'live' || trueStatus === 'connecting') push('reconnecting'); };
    };

    const reconnect = () => { push('reconnecting'); connect(); };

    // Zombie-SSE watchdog: only armed once we've seen a real ping (so a new client
    // against an old server that still sends `: ping` comments falls back to the
    // browser's own onerror handling and never reconnect-storms).
    const watchdog = setInterval(() => {
      if (!sawPing) return;
      // trueStatus is never 'paused' (that's a display-only overlay); a hidden but
      // live tab still reads 'live' here, so the watchdog still guards a backgrounded
      // zombie.
      if (trueStatus === 'live' && Date.now() - lastMessageAt > STALE_MS) reconnect();
    }, WATCHDOG_MS);

    // Back to the foreground: kick a dead stream, else catch up. While hidden there's
    // nothing to do — the "Paused" label is derived in ConnectionStatus, and the store
    // already holds the real status.
    const onVisible = () => {
      if (document.hidden) return;
      if (sawPing && Date.now() - lastMessageAt > STALE_MS) reconnect();
      else void catchUp();
    };
    const onOffline = () => { push('offline'); es?.close(); };
    // A network 'online' can fire without the stream ever dropping (VPN / Wi-Fi
    // handoff, wake-from-sleep) — don't tear down a healthy connection, just catch up.
    const onOnline = () => { if (trueStatus === 'live') void catchUp(); else reconnect(); };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    push('connecting');
    connect();

    return () => {
      disposed = true;
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      es?.close();
    };
  }, [applyEvent, applyCoarse, coarseInvalidateAll, setConnStatus, noteConnEvent]);

  return null;
}
